import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';

import AddNewBrand from '@components/AddNewBrand';
import CustomDropdown from '@components/CustomDropdown';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import { getBrands, getCollections, getSeasons } from '@store/design/action';
import { getAllBrands, getAllCollections, getAllSeasons } from '@store/reducers';

import { createNewStyle } from '@services/collectionService';

import ApiClient from '@lib/apiClient';

import { checkAnyUserType, getUserInfo, onPreventDefaultAll } from '@utils/Util';

const acceptedRoles = ['ADMIN', 'FASHION_DESIGNER'];
const onSetCollectionOwner = () => {
	return getUserInfo().primaryUserType === 'FASHION_DESIGNER';
};
const AddDesignToCollection = ({
	title,
	actionType,
	type,
	onClickNextAndBack,
	onAddDesign,
	designIdList,
	collectionDetails,
	onSubmitUploadedDesigns,
	onCancel,
	isMoveActive = true
}) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [brandModal, setBrandModal] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isBuyerSelectionDisabled, setIsBuyerSelectionDisabled] = useState(false);
	const [selectedCollection, setSelectedCollection] = useState({});
	const [brandName, setBrandName] = useState({});
	const [isNewCollection, setIsNewCollection] = useState(false);
	const [errors, setErrors] = useState({});
	const [designInfo, setDesignInfo] = useState({
		brandId: null,
		collectionId: null
	});
	const dispatch = useDispatch();
	const { id } = useParams();
	const collectionRef = useRef();
	const submitRef = useRef();
	const isGlobalLoading = useSelector((store) => store.loaderStore);

	let collections = useSelector(getAllCollections);
	collections = collections.map((item) => {
		return {
			id: item.id,
			text: item.collectionName,
			value: item.id,
			label: item.collectionName,
			brandId: item?.brandId || null,
			brandName: item?.brand || '',
			isNitexCollection: 'isNitexCollection' in item ? item.isNitexCollection : onSetCollectionOwner(),
			isBuyerSelection: 'isBuyerSelection' in item ? item.isBuyerSelection : null,
			season: item?.season || ''
		};
	});

	let brands = useSelector(getAllBrands);
	brands = brands.map((item) => {
		return {
			id: item.id,
			value: item.name,
			docUrl: item.brandLogoDocResponse.docUrl
		};
	});
	let seasons = useSelector(getAllSeasons);
	seasons = seasons.map((item) => {
		return {
			id: item.code,
			value: item.name
		};
	});

	const resetValidation = (errorType) => {
		if (errorType === 'collectionError') {
			setErrors({ ...errors, collectionError: '' });
		} else if (errorType === 'typeError') {
			setErrors({ ...errors, typeError: '' });
		} else {
			setErrors({ ...errors, brandError: '' });
		}
	};

	useEffect(() => {
		dispatch(getCollections(searchTerm));
	}, [dispatch, searchTerm]);

	useEffect(() => {
		dispatch(getBrands()).finally(() => {
			setIsLoading(false);
		});
		dispatch(getSeasons()).finally(() => {
			setIsLoading(false);
		});
	}, [dispatch]);

	useEffect(() => {
		if (collectionDetails) {
			setSelectedCollection(collectionDetails);
			setBrandName({ value: collectionDetails?.brand });
			setDesignInfo({
				...designInfo,
				collectionId: collectionDetails.id,
				brandId: collectionDetails?.brandId,
				isNitexCollection: collectionDetails.isNitexCollection,
				isBuyerSelection: collectionDetails?.isBuyerSelection,
				season: collectionDetails?.season
			});
		}
	}, [collectionDetails]);

	useEffect(() => {
		collectionRef.current.focus();
		document.body.addEventListener('keydown', onKeyDown);
		return () => {
			document.body.removeEventListener('keydown', onKeyDown);
		};
	}, [dispatch]);

	const onKeyDown = (e) => {
		onPreventDefaultAll(e, document.activeElement, submitRef.current);
	};

	const onItemClick = (item) => {
		resetValidation('brandError');
		if (item?.title === 'Brand') {
			setBrandName(item);
			setDesignInfo({
				...designInfo,
				brandId: item.id
			});
		}
	};

	const setNewBrandToSelectedBrand = async (id) => {
		if (id) {
			setIsLoading(true);
			await ApiClient.GET('getBrandInfo', id)
				.then(({ data }) => {
					setBrandName({ value: data.name });
					setDesignInfo({
						...designInfo,
						brandId: data.id
					});
					setIsLoading(false);
				})
				.catch(({ response }) => {
					setIsLoading(false);
					toastError(response.data.message);
				});
		}
	};
	const triggerBrandModal = () => {
		setBrandModal(!brandModal);
	};

	const handleChange = (e) => {
		if (e.target.name === 'isNitexCollection') {
			resetValidation('typeError');
			setDesignInfo({
				...designInfo,
				isNitexCollection: e.target.value === 'yes' ? false : e.target.value === 'no' ? true : null
			});
		} else {
			setDesignInfo({
				...designInfo,
				[e.target.id]: e.target.value
			});
		}
	};

	const isValidForm = () => {
		if (!designInfo.collectionId && !isNewCollection) {
			setErrors({ ...errors, collectionError: 'Collection is required' });
			return false;
		}
		if (!selectedCollection.name && isNewCollection) {
			setErrors({ ...errors, collectionError: 'Collection is required' });
			return false;
		}
		if (!designInfo.brandId && isNewCollection) {
			setErrors({ ...errors, brandError: 'Brand is required' });
			return false;
		}
		// if (acceptedRoles.includes(userRole) && designInfo.isNitexCollection === null && isNewCollection) {
		// 	setErrors({ ...errors, typeError: 'Collection type is required' });
		// 	return false;
		// }
		return true;
	};
	const onAddSuccess = () => {
		onAddDesign();
	};

	const addProductToCollection = async (collectionId) => {
		setIsLoading(true);
		let body = {
			id: designInfo.collectionId || collectionId,
			productIds: [...designIdList]
		};
		const optionBody = {
			collectionId: designInfo.collectionId || collectionId,
			productIdList: [...designIdList]
		};
		await ApiClient.POST('createProductsOption', optionBody)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					onAddSuccess(body.id);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const moveProductToCollection = async (collectionId) => {
		setIsLoading(true);
		let body = {
			currentCollectionId: parseInt(id),
			newCollectionId: isNewCollection ? collectionId : designInfo?.collectionId,
			productIds: [...designIdList]
		};
		await ApiClient.POST(
			'moveToCollection',
			{},
			`?currentCollectionId=${body.currentCollectionId}&newCollectionId=${body.newCollectionId}&productIds=${body.productIds}`
		)
			.then(({ data }) => {
				setIsLoading(false);
				if (data.success) {
					onAddDesign();
					//navigate(`/collections/view/` + body.newCollectionId);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onSubmitDesigns = async (collectionId = null) => {
		if (type === 'ADD_NEW') {
			addProductToCollection(collectionId);
		} else {
			onSubmitUploadedDesigns(designInfo?.collectionId || collectionId);
		}
	};

	const onAddNewCollection = async (type) => {
		setIsLoading(true);
		await ApiClient.POST('addCollection', {
			brandId: designInfo.brandId,
			isNitexCollection: designInfo.isNitexCollection,
			name: selectedCollection.name,
			season: designInfo.season,
			privacy: 'CUSTOM',
			isBuyerSelection: designInfo.isBuyerSelection
		})
			.then(({ data }) => {
				setIsLoading(false);
				if (data.success) {
					setDesignInfo((prev) => ({ ...prev, collectionId: data.id }));
					toastSuccess(data.message);
					if (type === 'COPY') {
						onSubmitDesigns(data.id);
					} else {
						moveProductToCollection(data.id);
					}
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const onCreateNewStyle = async () => {
		const isValid = isValidForm();
		if (!isValid) return;
		setIsLoading(true);
		await createNewStyle({
			id: designInfo.collectionId,
			productIds: [...designIdList]
		});
		onAddDesign();
		setIsLoading(false);
	};

	const onSubmit = () => {
		const isValid = isValidForm();
		if (!isValid) return;
		if (isNewCollection) {
			onAddNewCollection('COPY');
		} else {
			onSubmitDesigns();
		}
	};
	const onMoveSubmit = (collectionId) => {
		const isValid = isValidForm();
		if (!isValid) return;
		if (isNewCollection && !collectionId) {
			onAddNewCollection('MOVE');
		} else {
			moveProductToCollection(collectionId);
		}
	};

	const onChangeCollection = (selected) => {
		resetValidation('collectionError');
		if (selected?.__isNew__) {
			setIsNewCollection(true);
			setIsBuyerSelectionDisabled(false);
			setSelectedCollection({
				name: selected?.label,
				id: selected?.value
			});
			setDesignInfo({
				...designInfo,
				isNitexCollection: onSetCollectionOwner(),
				isBuyerSelection: selected?.isBuyerSelection
			});
			return {
				text: selected.label
			};
		} else {
			setIsNewCollection(false);
			setIsBuyerSelectionDisabled(true);
			setBrandName({ value: selected?.brandName });
			setSelectedCollection({
				name: selected?.label,
				id: selected?.value
			});
			setDesignInfo({
				...designInfo,
				collectionId: selected?.id,
				brandId: selected?.brandId,
				isNitexCollection: selected?.isNitexCollection,
				season: selected?.season,
				isBuyerSelection: selected?.isBuyerSelection
			});
			return {
				text: selected?.label,
				id: selected?.id
			};
		}
	};

	const getSelectedValue = () => {
		if (selectedCollection.name) {
			return {
				label: selectedCollection?.name,
				value: selectedCollection?.id
			};
		} else {
			return null;
		}
	};

	return (
		<>
			<Modal
				show={true}
				centered
				animation={true}
				backdrop="static"
				onHide={onCancel}
				className="auto-width-popup"
			>
				<LoaderComponent loading={isLoading}>
					<div className="common-popup upload-design-popup shadow-24dp">
						<Modal.Header closeButton>
							<h3 className="semibold-16 mb-0">{title}</h3>
						</Modal.Header>
						<Modal.Body>
							<div className="common-popup-body">
								<div className="container px-0">
									<div className="row">
										<div className="col-md-12 mb-2">
											<div className="form-group no-shadow-react-select size36 mb-0">
												<label htmlFor="strengthList">Collection</label>
												<CreatableSelect
													formatCreateLabel={(userInput) => (
														<>
															<span
																style={{
																	fontWeight: 'bold',
																	paddingRight: '3px'
																}}
															>
																Create new:
															</span>{' '}
															<span style={{ color: '#21242B' }}>
																{userInput}
															</span>
														</>
													)}
													ref={collectionRef}
													placeholder="Search a collection or create new..."
													isClearable
													onChange={onChangeCollection}
													options={collections}
													value={getSelectedValue()}
													onInputChange={(value) => setSearchTerm(value)}
												/>
												{errors.collectionError && (
													<p className="error">{errors.collectionError}</p>
												)}
											</div>
										</div>
										<div
											className={`col-md-${
												checkAnyUserType(acceptedRoles) ? '6  pr-1' : '12'
											}`}
										>
											{!isNewCollection ? (
												<div className="form-group">
													<label htmlFor="brand">Brand</label>
													<input
														type="text"
														autoComplete="off"
														name="brand"
														id="brand"
														placeholder={brandName?.value || 'Select...'}
														disabled
													/>
												</div>
											) : (
												<div className="form-group">
													<label htmlFor="brand">Brand</label>
													<CustomDropdown
														type="addItem"
														title="Brand"
														items={brands}
														selectedItem={brandName.value}
														onItemClick={(value) => {
															if (value.isAddNew) {
																triggerBrandModal();
															} else {
																onItemClick(value);
															}
														}}
														// isAddNew={true}
													/>
												</div>
											)}
											{errors.brandError && (
												<p className="error">{errors.brandError}</p>
											)}
										</div>
										{checkAnyUserType(acceptedRoles) && (
											<div className="col-md-6 pl-1">
												<div className="form-group select-component pb-2">
													<label htmlFor="market">Season</label>
													{!isNewCollection ? (
														<input
															type="text"
															autoComplete="off"
															name="brand"
															id="brand"
															placeholder={designInfo?.season || 'Select...'}
															disabled
														/>
													) : (
														<select
															className="w-100"
															name="season"
															id="season"
															onChange={handleChange}
														>
															<option>Select</option>
															{seasons.map((item) => (
																<option value={item.id} key={item.id}>
																	{item.value}
																</option>
															))}
														</select>
													)}
												</div>
											</div>
										)}
										<div className="col-md-12">
											<div className="form-group radio-component mb-0">
												<label htmlFor="isNitexCollection" className="gray_dark_01">
													Buyer techpack?
												</label>
												<input
													type="radio"
													name="isNitexCollection"
													value={'yes'}
													checked={designInfo.isNitexCollection === false}
													onChange={handleChange}
													disabled={!isNewCollection}
												/>
												<label htmlFor="yes" className="gray_dark_01">
													Yes
												</label>
												<input
													type="radio"
													name="isNitexCollection"
													value={'no'}
													checked={designInfo.isNitexCollection === true}
													onChange={handleChange}
													disabled={!isNewCollection}
												/>
												<label htmlFor="no" className="gray_dark_01">
													No
												</label>
												{errors.typeError && (
													<p className="error">{errors.typeError}</p>
												)}
											</div>
										</div>
										<div className="col-md-12 pt-2">
											<div className="custom-chekbox">
												<div className="form-group m-0">
													{isBuyerSelectionDisabled ? (
														<input
															type="checkbox"
															checked={designInfo?.isBuyerSelection === true}
															disabled={true}
														/>
													) : (
														<input
															type="checkbox"
															id="isBuyerSelection"
															name="isBuyerSelection"
															checked={designInfo?.isBuyerSelection === true}
															onChange={() => {
																setDesignInfo({
																	...designInfo,
																	isBuyerSelection:
																		!designInfo.isBuyerSelection
																});
															}}
														/>
													)}
													<label htmlFor="isBuyerSelection" className="m-0">
														<span className="align-middle">
															This collection is the buyer&#39;s selection.
															{designInfo?.isBuyerSelection}
														</span>
													</label>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</Modal.Body>
						<div className="modal-footer align-items-center justify-content-between">
							<button
								className="button text-only"
								onClick={() => onCreateNewStyle()}
								disabled={isLoading}
							>
								<span>Create as new style</span>
							</button>
							<div className="footer-buttons text-right">
								<div>
									{type === 'UPLOAD_DESIGN' ? (
										<button
											className="button gray-outline size32"
											onClick={onClickNextAndBack}
										>
											<img
												src={`${process.env.PUBLIC_URL}/icons/Left-arrow.svg`}
												alt="back"
											/>
											<span>Back</span>
										</button>
									) : (
										''
									)}

									{type === 'UPLOAD_DESIGN' ? (
										<button
											className="button tertiary size32 ml-2"
											onClick={() => onSubmit()}
											ref={submitRef}
											disabled={isLoading || isGlobalLoading}
										>
											Submit
										</button>
									) : (
										<>
											{actionType === 'COPY' ? (
												<button
													className="button primary size32"
													onClick={() => onSubmit()}
													disabled={isLoading}
												>
													<span>Create option</span>
												</button>
											) : (
												<>
													{isMoveActive && (
														<button
															className="button text size32 mr-2"
															onClick={() =>
																// moveProductToCollection(designInfo.collectionId)
																onMoveSubmit(designInfo?.collectionId)
															}
															disabled={isLoading}
														>
															Move
														</button>
													)}
													<button
														className="button primary size32"
														disabled={isLoading}
														onClick={() => onSubmit()}
													>
														<span className="">Create option</span>
													</button>
												</>
											)}
										</>
									)}
								</div>
							</div>
						</div>
					</div>
				</LoaderComponent>
			</Modal>

			<Modal show={brandModal} onHide={() => triggerBrandModal} size="lg" centered>
				<AddNewBrand
					brandModal={brandModal}
					triggerBrandModal={triggerBrandModal}
					setNewBrandToSelectedBrand={setNewBrandToSelectedBrand}
				/>
			</Modal>
		</>
	);
};

export default AddDesignToCollection;
